'use client';

import React from 'react';
import { Link2, Printer, Share2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import type { PublicRecord } from '@/types/publicRecords';
import { buildCertificateDocumentMeta } from '@/lib/certificateDocument';

export default function PublicCertificateToolbar({ record }: { record: PublicRecord }) {
  const meta = buildCertificateDocumentMeta(record);
  const canNativeShare =
    typeof navigator !== 'undefined' &&
    typeof (navigator as Navigator & { share?: unknown }).share === 'function';

  const handlePrint = () => {
    window.print();
    toast.message('Save as PDF', {
      description: 'Choose “Save as PDF” or “Microsoft Print to PDF” in the print dialog.',
    });
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(meta.verifyUrl);
      toast.success('Verification link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleShare = async () => {
    try {
      await (
        navigator as Navigator & { share: (data: ShareData) => Promise<void> }
      ).share({
        title: `${record.title} — Ertzyx verified credential`,
        url: meta.verifyUrl,
      });
    } catch (err) {
      // AbortError just means the user closed the native share sheet.
      if (err instanceof Error && err.name === 'AbortError') return;
      void handleCopy();
    }
  };

  return (
    <div className="certificate-no-print mb-6 flex flex-wrap gap-3">
      <button
        type="button"
        onClick={handlePrint}
        className="inline-flex items-center gap-2 rounded-xl bg-trust-blue px-4 py-2.5 text-sm font-semibold text-white shadow-sm"
      >
        <Printer size={16} />
        Download / Print PDF
      </button>
      {canNativeShare && (
        <button
          type="button"
          onClick={() => void handleShare()}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700"
        >
          <Share2 size={16} />
          Share
        </button>
      )}
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700"
      >
        <Link2 size={16} />
        Copy verify link
      </button>
      <a
        href={meta.verifyPath}
        className="inline-flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900"
      >
        <ShieldCheck size={16} />
        Open verify page
      </a>
    </div>
  );
}
